import type { User } from '@/types/User'

const USER_KEY = 'user_data'
const AUTH_KEY = 'is_authenticated'

export const tokenService = {
    /**
     * Set authenticated status
     * @param status - true to login, false to logout
     * @param remember - if true, use localStorage (persistent). Default false (sessionStorage)
     */
    setAuthenticated(status: boolean, remember: boolean = false) {
        if (status) {
            if (remember) {
                localStorage.setItem(AUTH_KEY, 'true')
                sessionStorage.removeItem(AUTH_KEY) // Clear session to avoid dupes
            } else {
                sessionStorage.setItem(AUTH_KEY, 'true')
                localStorage.removeItem(AUTH_KEY) // Clear local to avoid dupes
            }
        } else {
            sessionStorage.removeItem(AUTH_KEY)
            localStorage.removeItem(AUTH_KEY)
        }
    },

    /**
     * Check if user is authenticated
     * Checks localStorage first, then sessionStorage
     */
    isAuthenticated(): boolean {
        return localStorage.getItem(AUTH_KEY) === 'true' || sessionStorage.getItem(AUTH_KEY) === 'true'
    },

    /**
     * Check if the session is persistent (localStorage)
     */
    isPersistent(): boolean {
        return localStorage.getItem(AUTH_KEY) === 'true'
    },

    /**
     * Store user info
     * Infers storage location based on where AUTH_KEY is stored
     */
    setUser(user: User) {
        // If we are persistent (localStorage has auth), store user there. Otherwise session.
        if (localStorage.getItem(AUTH_KEY) === 'true') {
            localStorage.setItem(USER_KEY, JSON.stringify(user))
            sessionStorage.removeItem(USER_KEY)
        } else {
            sessionStorage.setItem(USER_KEY, JSON.stringify(user))
            localStorage.removeItem(USER_KEY)
        }
    },

    /**
     * Get user info
     */
    getUser(): User | null {
        const localUser = localStorage.getItem(USER_KEY)
        if (localUser) return JSON.parse(localUser)

        const sessionUser = sessionStorage.getItem(USER_KEY)
        return sessionUser ? JSON.parse(sessionUser) : null
    },

    /**
     * Clear all auth data
     * (Tokens are handled by httpOnly cookies)
     */
    clearAuthState() {
        sessionStorage.removeItem(AUTH_KEY)
        sessionStorage.removeItem(USER_KEY)
        localStorage.removeItem(AUTH_KEY)
        localStorage.removeItem(USER_KEY)
    }
}
